/**
 * The "must be there by" date on a pickup or delivery.
 *
 * Only some location types can carry one, and errors are computed only when
 * the section is actually shown — a stale deadline left behind by Repeat
 * Shipment would otherwise block the screen with a message the user can't see.
 *
 * Usually optional, asked as a yes/no first. For a brand that always works to a
 * date (Walmart) it isn't a question at all: there is no toggle, and the date is
 * required. It starts empty on purpose — the only date worth having is the one on
 * the customer's purchase order, and any value we invent is indistinguishable from
 * that once it reaches Operations.
 */

import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store";
import {
  errorMessageForHasDeadline,
  locationsErrorMessageForDeadline,
} from "@/Shared/Locations/Validators/locationsErrorMessageForDeadline";
import { bookShipmentDeadlineIsRequired } from "../Helpers/bookShipmentDeadlineIsRequired";
import { bookShipmentShouldShowDeadlineSection } from "../Helpers/bookShipmentShouldShowDeadlineSection";
import {
  setDeliveryDeadline,
  setHasDeliveryDeadline,
  setHasPickupDeadline,
  setPickupDeadline,
} from "@/Slices/bookShipmentSlice";

interface BookShipmentAddressDeadlineInput {
  context: LocationContext;
  location: PreQuoteLocation;
  pickupDate: string;
  selectedQuote: Quote | undefined;
}

export function useBookShipmentAddressDeadline(
  input: BookShipmentAddressDeadlineInput,
) {
  const { context, location, pickupDate, selectedQuote } = input;
  const dispatch = useDispatch();
  const isPickup = context === LocationContext.Pickup;

  const deadlineDate = useSelector((s: RootState) =>
    isPickup
      ? s.bookShipment.shipment.pickupDeadline
      : s.bookShipment.shipment.deliveryDeadline,
  );

  const deadlineIsSet = useSelector((s: RootState) =>
    isPickup
      ? s.bookShipment.shipment.pickupDeadline !== undefined
      : s.bookShipment.shipment.deliveryDeadline !== undefined,
  );

  const deadlineIsRequired = bookShipmentDeadlineIsRequired(location, context);

  // There is no "no deadline" answer to give for a required brand, so the yes/no
  // is never asked and the answer is always yes
  const hasDeadline = deadlineIsRequired ? true : deadlineIsSet;

  function onHasDeadlineChange(value: boolean) {
    if (isPickup) {
      dispatch(setHasPickupDeadline(value));
      return;
    }
    dispatch(setHasDeliveryDeadline(value));
  }

  function onDeadlineDateChange(value: moment.Moment | undefined) {
    const date = value?.format("YYYY-MM-DD");
    if (isPickup) {
      dispatch(setPickupDeadline(date));
      return;
    }
    dispatch(setDeliveryDeadline(date));
  }

  // Only generate deadline errors if the deadline section would be shown
  // This prevents stale deadline values (e.g., from "Repeat Shipment") from causing
  // hidden validation failures when the destination doesn't support deadlines
  const showDeadlineSection = bookShipmentShouldShowDeadlineSection(
    location,
    context,
  );

  // The carrier's earliest delivery date only constrains delivery deadlines,
  // not pickup deadlines
  const earliestDeliveryDate = isPickup
    ? undefined
    : selectedQuote?.expectedDeliveryDate;

  const deadlineErrors = showDeadlineSection
    ? locationsErrorMessageForDeadline(
        pickupDate,
        hasDeadline,
        deadlineDate,
        earliestDeliveryDate,
      )
    : undefined;

  const hasDeadlineErrors = showDeadlineSection
    ? errorMessageForHasDeadline(location.locationType, hasDeadline)
    : undefined;

  // What the server should be told. A deadline this screen never asked about
  // must not be forwarded just because it is still sitting in Redux — both
  // writers of the field send this, not the raw value
  const deadlineToSubmit = showDeadlineSection ? deadlineDate : undefined;

  return {
    showDeadlineSection,
    deadlineIsRequired,
    deadlineDate,
    deadlineToSubmit,
    hasDeadline,
    earliestDeliveryDate,
    deadlineErrors,
    hasDeadlineErrors,
    onHasDeadlineChange,
    onDeadlineDateChange,
  };
}
